import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL, CountryOptions } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, Building2 } from 'lucide-react';

const EMPTY_FORM = {
  name: '',
  email: '',
  telephone: '',
  domain: '',
  city: '',
  country: '',
  address: '',
  fax: '',
  training_center_id: '',
  category: '',
  primary_contact: '',
};

const CompanyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  usePageTitle('Edit Company');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [companyRes, categoriesRes, centersRes] = await Promise.all([
          axios.get(`${API_URL}/companies/${id}`),
          axios.get(`${API_URL}/company-categories`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/training-centers`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const c = companyRes.data;
        setFormData({
          name: c.name || '',
          email: c.email || '',
          telephone: c.telephone || '',
          domain: c.domain || '',
          city: c.city || '',
          country: c.country || '',
          address: c.address || '',
          fax: c.fax || '',
          training_center_id: c.training_center_id || '',
          category: c.category || '',
          primary_contact: c.primary_contact || '',
        });
        const cats = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
        setCategoryOptions(
          cats.map((cat) => ({
            value: cat.name || cat,
            label: cat.name || cat,
          }))
        );
        setTrainingCenters(Array.isArray(centersRes.data) ? centersRes.data : []);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error('Failed to load company:', err);
          toast.error('Could not load the company.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a company name');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') payload[k] = null;
      });
      payload.name = formData.name.trim();
      await axios.put(`${API_URL}/companies/${id}`, payload);
      toast.success('Company updated');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to update company';
      toast.error(typeof detail === 'string' ? detail : 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-[#2C9DE1]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center space-y-4">
        <Building2 className="w-12 h-12 mx-auto text-slate-300" />
        <h1
          className="text-2xl font-bold text-slate-900"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Company not found
        </h1>
        <p className="text-slate-500">
          The company you are looking for may have been deleted or merged.
        </p>
        <Button onClick={() => navigate('/companies')} data-testid="back-to-companies-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Companies
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="company-edit-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/companies')}
            className="mb-2 -ml-2"
            data-testid="back-to-list-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Edit Company
          </h1>
          <p className="text-slate-500 mt-1">Update the details below and save changes.</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="form-section-header">Company Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Company Ltd"
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category || 'none'}
                    onValueChange={(value) =>
                      setFormData({ ...formData, category: value === 'none' ? '' : value })
                    }
                  >
                    <SelectTrigger data-testid="company-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No category</SelectItem>
                      {categoryOptions.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="info@company.com"
                    data-testid="company-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="company.com"
                    data-testid="company-domain-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telephone">Telephone</Label>
                  <Input
                    id="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    placeholder="+357 22 123456"
                    data-testid="company-telephone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fax">Fax</Label>
                  <Input
                    id="fax"
                    value={formData.fax}
                    onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                    placeholder="+357 22 123457"
                    data-testid="company-fax-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="primary_contact">Primary Contact</Label>
                <Input
                  id="primary_contact"
                  value={formData.primary_contact}
                  onChange={(e) =>
                    setFormData({ ...formData, primary_contact: e.target.value })
                  }
                  placeholder="John Smith"
                  maxLength={200}
                  data-testid="company-primary-contact-input"
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="form-section-header">Location</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={formData.country || 'none'}
                    onValueChange={(value) =>
                      setFormData({ ...formData, country: value === 'none' ? '' : value })
                    }
                  >
                    <SelectTrigger data-testid="company-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select country</SelectItem>
                      {CountryOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Nicosia"
                    data-testid="company-city-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                  data-testid="company-address-input"
                />
              </div>
            </div>

            {/* Training Center */}
            <div className="space-y-4">
              <h3 className="form-section-header">Training Center</h3>

              <div className="space-y-2">
                <Label htmlFor="training_center">Assigned Training Center</Label>
                <Select
                  value={formData.training_center_id || 'none'}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      training_center_id: value === 'none' ? '' : value,
                    })
                  }
                >
                  <SelectTrigger data-testid="company-trainingcenter-select">
                    <SelectValue placeholder="Select a training center" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No training center</SelectItem>
                    {trainingCenters.map((center) => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/companies')}
                data-testid="cancel-edit-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
                data-testid="company-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanyEdit;
